/// <amd-module name="Tfs/Platform/Navigation" />
import { ITfsNavigationSettingsService } from "Tfs/Platform/NavigationSettings";
import { ITfsPageService } from "Tfs/Platform/Page";
import Resources = require("Tfs/Platform/Resources");
import { IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssObservableService, IVssPageContext, ServiceOptions, Services, VssObservableService } from "VSS/Platform/Context";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { routeUrl } from "VSS/Platform/Location";
import { IContributedNavigation, IVssNavigationService, NavigationFlags } from "VSS/Platform/Navigation";
import { IVssPageService } from "VSS/Platform/Page";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { getCookie, setCookie } from "VSS/Platform/Util/Cookie";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";

const projectSettingsRoute = "ms.vss-admin-web.project-admin-hub-route";

/**
 * Names of events triggered by the ITfsNavigationService
 */
export namespace TfsNavigationServiceEventNames {
    /**
     * Event triggered when a navigation element has been modified
     */
    export const navigationElementModified = "navigationElementModified";
}

/**
 * Indices for the different TFS Navigation element types (zero-based)
 */
export const enum TfsNavigationElementLevels {
    Page = 0,
    HubGroupCollection = 1,
    HubGroup = 2,
    Hub = 3
}

/**
 * Event object sent in events where the navigation properties changed
 */
export interface INavigationElementsModifiedEvent {
    updatedNavigation: IContributedNavigation;
}

const DisplayModeCookie = "vmExpanded";

/**
 * Display mode for the vertical navigation component
 */
export const enum NavigationDisplayMode {
    Collapsed = 1,
    Expanded = 2
}

/**
 * Service to manage tfs specific navigation updates
 *
 * Events:
 *    updatedNavigation: INavigationElementsModifiedEvent
 *                 Triggered when a navigation element has been modified
 */
export interface ITfsNavigationService
    extends IVssObservableService<INavigationElementsModifiedEvent | NavigationDisplayMode, "navigationElementModified" | "displayModeChanged"> {
    /**
     * Looks in local storage to find the last hub that was visited for the
     * specified hub group and returns it (or undefined if no entry yet).
     *
     * @param hubGroupNavElement - The navigation element for the hub group to check
     *
     * @returns IContributedNavigation of the hub that was last accessed
     */
    getMostRecentHubForHubGroup(hubGroupNavElement: IContributedNavigation): IContributedNavigation | undefined;

    /**
     * Sets the id of the most recently accessed hub within a given hub group
     *
     * @param hubGroupId - Id of the hub group
     * @param hubId - Id of the hub
     */
    setMostRecentHubForHubGroup(hubGroupId: string, hubId: string): void;

    /**
     * To set the title of the page to refer to something specific, the caller
     * should pass a string that represents the object. This method will ensure
     * the product name is appropriately added.
     */
    setPageTitle(pageTitle: string): void;

    /**
     * Gets the current display mode for the vertical navigation component
     */
    getNavigationDisplayMode(): IReadonlyObservableValue<NavigationDisplayMode>;

    /**
     * Sets the display mode for the vertical navigation component
     */
    setNavigationDisplayMode(mode: NavigationDisplayMode): void;

    /**
     * Navigates to the specified navigations id.
     *
     * @param navigationId
     */
    navigateToNavElement(navigationId: string): void;

    /**
     * Navigates to view considered home view for a project
     */
    navigateToHome(): void;

    /**
     * Navigates to the specifies route
     *
     * @param routeId - Route to navigate to.
     * @param includeProject - If true add project info to the route params
     */
    navigateToRoute(routeId: string, includeProject?: boolean): void;
}

class TfsNavigationService
    extends VssObservableService<INavigationElementsModifiedEvent | NavigationDisplayMode, "navigationElementModified" | "displayModeChanged">
    implements ITfsNavigationService {
    private titleSuffix: string;
    private navigationDisplayMode?: ObservableValue<NavigationDisplayMode>;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        const navigationService = pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const displayedNavigation = navigationService.getDisplayedNavigation();

        // In vertical navigation, the document title suffix is the name of the hub group
        if (displayedNavigation.length > TfsNavigationElementLevels.Hub) {
            const hubGroup = displayedNavigation[TfsNavigationElementLevels.HubGroup];
            const hubGroupNav = navigationService.getNavigation(hubGroup);
            if (hubGroupNav && hubGroupNav.name) {
                this.titleSuffix = hubGroupNav.name;
            }
        }

        if (!this.titleSuffix) {
            const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData();
            this.titleSuffix = pageData && pageData.isHosted ? Resources.ApplicationTitleHosted : Resources.ApplicationTitleOnPrem;
        }

        const pageTitle = navigationService.getPageTitle();
        if (pageTitle) {
            this.setPageTitle(pageTitle);
        }

        // Set the user's most recently accessed hub (level 3) within this hub group (level 2).
        if (displayedNavigation.length > TfsNavigationElementLevels.Hub) {
            const hubNavigation = navigationService.getNavigation(displayedNavigation[TfsNavigationElementLevels.Hub]);
            if (hubNavigation && (hubNavigation.flags! & NavigationFlags.Hidden) === 0) {
                this.setMostRecentHubForHubGroup(
                    displayedNavigation[TfsNavigationElementLevels.HubGroup],
                    displayedNavigation[TfsNavigationElementLevels.Hub]
                );
            }
        }
    }

    public getMostRecentHubForHubGroup(hubGroupNavElement: IContributedNavigation): IContributedNavigation | undefined {
        const mruHubId: string | null = tryGetLocalStorageValue("lastHub/" + hubGroupNavElement.id, null);

        let recentHubNavElement: IContributedNavigation | undefined;
        if (mruHubId && hubGroupNavElement.children) {
            recentHubNavElement = hubGroupNavElement.children.filter(n => n.id === mruHubId)[0];

            // the children for the group nav element does not contain hidden groups. For the old dashboards experience
            // we would need to look it up in the hidden children
            if (mruHubId === "ms.vss-dashboards-web.dashboards-hub") {
                const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
                recentHubNavElement = navigationService.getNavigation(mruHubId);
            }
        }

        return recentHubNavElement;
    }

    public setMostRecentHubForHubGroup(hubGroupId: string, hubId: string): void {
        trySetLocalStorageValue("lastHub/" + hubGroupId, hubId);
    }

    public setPageTitle(pageTitle: string) {
        document.title = format(Resources.DocumentTitle, pageTitle, this.titleSuffix);
    }

    public getNavigationDisplayMode(): IReadonlyObservableValue<NavigationDisplayMode> {
        if (this.navigationDisplayMode === undefined) {
            // Compute the displayMode based on the userSetting (auth'd members) or cookie.
            let displayMode: NavigationDisplayMode | undefined = undefined;

            // If the current user has settings in the project we will look if they have a vertical navigation preference.
            if (userHasClaim(this.pageContext, UserClaims.Member)) {
                const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
                displayMode = settingsService.getEntry<NavigationDisplayMode>("navigation/verticalMenuExpanded", SettingsUserScope.Me);
            }

            // Check if we're on the project settings page and default to Collapsed
            const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
            const currentRoute = navigationService.getCurrentRoute();
            if (currentRoute.routeId === projectSettingsRoute) {
                displayMode = NavigationDisplayMode.Collapsed;
            }

            // If they aren't a member or dont have a setting check the cookie.
            if (displayMode === undefined) {
                const cookieValue = getCookie(DisplayModeCookie);
                if (cookieValue && cookieValue.length > 0) {
                    displayMode = Number.parseInt(cookieValue);
                }
            }

            this.navigationDisplayMode = new ObservableValue(displayMode || NavigationDisplayMode.Expanded);
        }

        return this.navigationDisplayMode;
    }

    public setNavigationDisplayMode(mode: NavigationDisplayMode): void {
        if (!this.navigationDisplayMode) {
            this.navigationDisplayMode = new ObservableValue(mode);
        } else {
            this.navigationDisplayMode.value = mode;
        }

        if (userHasClaim(this.pageContext, UserClaims.Member)) {
            const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
            settingsService.setEntries({ "navigation/verticalMenuExpanded": mode }, SettingsUserScope.Me);
        } else {
            setCookie(DisplayModeCookie, mode.toString());
        }

        this._notify(mode, "displayModeChanged");
    }

    public navigateToNavElement(navigationId: string): void {
        const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const contributedNavigation = navigationService.getNavigation(navigationId);
        if (contributedNavigation && contributedNavigation.href) {
            FastPageSwitch(this.pageContext, contributedNavigation.href, true);
        }
    }

    public navigateToRoute(routeId: string, includeProject?: boolean): void {
        const routeValues: { [key: string]: string } = {};
        if (includeProject) {
            const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
            const tfsPageData = tfsPageService.getData();
            if (tfsPageData && tfsPageData.project) {
                routeValues["project"] = tfsPageData.project.name;
            }
        }

        const url = routeUrl(this.pageContext, routeId, routeValues);
        if (url) {
            FastPageSwitch(this.pageContext, url, true);
        }
    }

    public navigateToHome(): void {
        this.navigateToRoute("ms.vss-tfs-web.suite-me-page-route");
    }

    private _remove(array: string[], item: string): boolean {
        const index = array.indexOf(item);
        if (index >= 0) {
            array.splice(index, 1);
        }
        return index >= 0;
    }
}

Services.add("ITfsNavigationService", { serviceFactory: TfsNavigationService, options: ServiceOptions.Startup });

export function isNavigationItemBuiltinOrTrusted(navigation: IContributedNavigation): boolean {
    return (
        navigation.flags !== undefined &&
        ((navigation.flags & NavigationFlags.BuiltIn) === NavigationFlags.BuiltIn ||
            (navigation.flags & NavigationFlags.Trusted) === NavigationFlags.Trusted) &&
        (navigation.flags & NavigationFlags.Hidden) !== NavigationFlags.Hidden
    );
}
